import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { UntypedFormBuilder, Validators } from '@angular/forms'
import { Subscription } from 'rxjs'
import { finalize, map, switchMap } from 'rxjs/operators'
import { Application } from 'src/app/account/services/application.service'
import { UnconfiguredS3BucketReplicationIssue } from 'src/app/account/services/issue'
import { S3BucketConfig, S3ObjectReplicationMode, SyncPair } from 'src/app/account/services/sync-pair.service'
import { ApiService } from 'src/app/services/api.service'
import { ResourceConfigDto, SyncPairIssueScopeDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { Http404Error } from 'src/utils/errors'
import { handleArpioError } from 'src/utils/handle-error'
import { ViewableIssue } from '../../issues-view/issues-view.component'
import { AccountService } from '../../../services/account.service'

@Component({
  selector: 'unconfigured-bucket-issue',
  templateUrl: './unconfigured-bucket-issue.component.html',
  styleUrls: ['./unconfigured-bucket-issue.component.scss'],
  standalone: false,
})
export class UnconfiguredBucketIssueComponent implements OnInit {
  @Input() issue: ViewableIssue = null
  @Input('sync-pair') syncPair: SyncPair = null
  @Input('application') application: Application = null

  @ViewChild('ConfirmManualReplicationConfigDialog', { static: true })
  confirmManualReplicationConfigDialog: ModalDialogComponent

  private _value: number = null
  private loadingSubscription: Subscription = null
  private savingSubscription: Subscription = null
  private saved = false
  private s3BucketConfig: S3BucketConfig = null
  constructor(
    public uiService: UiService,
    public apiService: ApiService,
    private formBuilder: UntypedFormBuilder,
    public accountService: AccountService
  ) {}

  ngOnInit(): void {
    let getSyncPairResourceConfig$ = !!this.syncPair
      ? this.syncPair.getSyncPairResourceConfig$(this.issue.arn)
      : this.application.getSyncPairResourceConfig$(this.issue.arn)
    this.loadingSubscription = getSyncPairResourceConfig$
      .pipe(finalize(() => (this.loadingSubscription = null)))
      .subscribe(
        (resourceConfig) => {
          if (!!resourceConfig) {
            this.s3BucketConfig = resourceConfig.resourceConfig as S3BucketConfig
            if (this.s3BucketConfig) {
              this.bucketConfigFormModel.setValue({
                backfillRange: Math.max(
                  this.backfillOptions.findIndex((e) => e.val == this.s3BucketConfig.backfillDays),
                  0
                ),
                customerManagesReplication: this.s3BucketConfig.customerManagesReplication ? 'true' : 'false',
              })
            }
          }
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  backfillOptions = [
    { val: 0, text: 'None' },
    { val: 90, text: 'Past 90 days' },
    { val: 180, text: 'Past 180 days' },
    { val: 365, text: 'Past year' },
    { val: 365 * 2, text: 'Past 2 years' },
    { val: 365 * 3, text: 'Past 3 years' },
    { val: 365 * 4, text: 'Past 4 years' },
    { val: 365 * 5, text: 'Past 5 years' },
    { val: -1, text: 'All objects' },
  ]

  bucketConfigFormModel = this.formBuilder.group({
    backfillRange: [(this.backfillOptions.length - 1).toString()],
    customerManagesReplication: ['false'],
    objectReplicationMode: [null, Validators.required],
  })

  saveClick() {
    if (this.customerManagesReplication) {
      this.confirmManualReplicationConfigDialog.open()
    } else {
      this.saveConfig()
    }
  }

  dialogContinueClick() {
    this.confirmManualReplicationConfigDialog.close()
    this.saveConfig()
  }

  dialogCancelClick() {
    this.confirmManualReplicationConfigDialog.close()
  }

  saveConfig() {
    let config = {
      resourceConfigType: 's3Bucket',
      backfillDays: this.backfillRange,
      customerManagesReplication: this.customerManagesReplication,
      objectReplicationMode: this.objectReplicationMode,
    } as ResourceConfigDto

    let saver$ = null
    if (this.syncPair) {
      saver$ = this.syncPair.setSyncPairResourceConfig$(this.issue.arn, config)
    } else {
      saver$ = this.application.setSyncPairResourceConfig$(this.issue.arn, config)
    }
    this.savingSubscription = saver$
      .pipe(
        switchMap((_config) => this.issue.deleter$()),
        finalize(() => (this.savingSubscription = null))
      )
      .subscribe(
        (_response) => {
          this.saved = true
          this.disableControls()
        },
        (err) => {
          handleArpioError(window.location.href, this.apiService, this.uiService)(err)
        }
      )
  }

  get backfillRange() {
    if (this.objectReplicationMode === S3ObjectReplicationMode.realTime) {
      return this.backfillOptions[this.bucketConfigFormModel.value.backfillRange].val
    } else {
      return -1
    }
  }

  get customerManagesReplication() {
    if (this.objectReplicationMode === S3ObjectReplicationMode.realTime) {
      return this.bucketConfigFormModel.value.customerManagesReplication === 'true'
    } else {
      return false
    }
  }

  get objectReplicationMode() {
    return this.bucketConfigFormModel.value.objectReplicationMode
  }

  get snapshotModeEnabled() {
    return this.accountService.entitledForCapability('s3SnapshotRestore')
  }

  get backfillRangeText() {
    return this.backfillOptions[this.bucketConfigFormModel.value.backfillRange].text
  }

  get saving() {
    return !!this.savingSubscription && !this.savingSubscription.closed
  }

  get saveStatus() {
    if (this.saving || this.confirmManualReplicationConfigDialog.isOpen) {
      return 'saving'
    }
    if (this.saved) {
      return 'saved'
    }
    return 'unsaved'
  }

  disableControls() {
    this.bucketConfigFormModel.disable()
  }

  get validConfig() {
    return !this.bucketConfigFormModel.invalid
  }
}
